/**
 * posture-brief.ts — Editorial copy layer for the Command Center standfirst.
 *
 * Composes the daily brief (action-first headline, lede, primary action,
 * verdict, provenance line) from the active posture state. The voice is "trusted
 * advisor": short sentences, no em-dashes, period-separated rather than
 * dash-bridged.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no seed, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data (score, delta, pillar scores, top action, critical count),
 * never from picking among synonymous phrasings of the same fact.
 *
 * Coherence rule: every slot (headline, verdict, lede) routes off the same
 * shared signals — `bucket` (the five-state delta bucket) for verdict
 * direction, and `dir` (a simple up/down/flat read of the rounded delta) for
 * the lede's delta clause — so no two slots can disagree about which way the
 * score is moving. The same holds for the pillar: the headline picks the first
 * move, and the verdict names a pillar only when it is the headline's pillar
 * AND the laggard, by its full name. When the ranker's top action sits on a
 * pillar other than the weakest, the verdict names no pillar ("Start with the
 * lead item."), because the Command Center shows both slots side by side.
 * The lede then says why the lowest pillar is not first: "**Baseline is the
 * lowest pillar, at 36**, but the queue's top fix is in Best Practice."
 *
 * Headline precedence (highest to lowest, see `pickActionHeadline`):
 *   1. topAction   — the priority-actions ranker's #1 item, by its pillar.
 *   2. Pillar-laggard — one pillar under 70 stands out.
 *   3. Critical count — one or more open critical items.
 *   4. Clear (score >= 85) or routine fallback.
 *
 * The brief narrates the operator queue rather than secretly reordering it:
 * if a class of fix (e.g. low MFA adoption) deserves to lead, that belongs in
 * the ranker so the queue visibly reflects it, not as an editorial override.
 *
 * Loading-state handling: `weakestPillar()` refuses to name a weakest when
 * any pillar input is `undefined`, so the brief never claims a pillar is
 * "the lowest" while another pillar is still streaming in (or when a pillar
 * is null because the tenant has no starred frameworks). The score-level
 * loading guard (`input.score == null`) sits above this and short-circuits
 * to the neutral "Reading posture…" brief.
 */

import {
  briefProvenance,
  bucketDelta,
  cap,
  controlsLedeBody,
  driftedLedeBody,
  FIRST_ASSESSMENT,
  firstAssessmentAction,
  isNegative,
  knownCount,
  LEDE_FRAGMENTS,
  mfaLedeBody,
  type DeltaBucket,
  type ScanScheduleState,
} from './brief-helpers'
import type { ActionBriefFields } from './brief-shape'

export type Pillar = 'compliance' | 'bpa' | 'identity'
export type Severity = 'crit' | 'high' | 'med' | 'low' | 'routine'

export interface PostureBrief {
  /** Six words or fewer. The Command Center shows it on the Guardian Score tile, upright and unquoted (D22). */
  verdict: string
  /** The provenance line (rule 4, D36): "Assessed by Guardian at 06:12 today", or "" while the time is unknown. Kept under its old name for PillarBrief. */
  byline: string
  /** Score delta vs N days ago. The Command Center draws its delta tag from the score history, not from this field. */
  delta: { value: number; days: number } | null
  /* Action-first fields (blueprint §4.7, §6.2). `composePostureBrief` always
   * sets `headline` and `lede`; they are optional on this type only because
   * inline composers outside `lib` still build PostureBrief literals. */
  /** The h1: action first, period-terminated. */
  headline?: string
  /** Substring of `headline` set at 400; `pillar` only when the text is that pillar's name. */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence with exactly one `**bold**` run naming the subject and its count. */
  lede?: string
  /** The view's one Soft Green button. */
  primaryAction?: ActionBriefFields['primaryAction']
}

export interface PriorityAction {
  id: string
  rank: number
  title: string
  pillar: Pillar
  severity: Severity
  meta: string
  href: string
  /** Optional external "learn more" URL — typically Microsoft Learn for BPA
   *  rows. Renders as a small icon affordance the user can click to open in
   *  a new tab; the row's primary click target stays on `href` (in-app). */
  learnMoreUrl?: string | null
  /** Computed impact score from the server-side ranking formula. Optional —
   *  exposed for production debugging tooltips, not consumed by default
   *  rendering. See operations-priority-alerts-curation.md §4.4. */
  impact?: number
  /** Number of underlying entities behind this action (users, sites, etc.). */
  scope?: number
  /** Distinct frameworks affected by the underlying policy/drift group. */
  frameworksAffected?: number
  /** Estimated Guardian Score lift in percentage points if this action is closed.
   *  Null for items not policy-mapped (drift, identity templates) or when the
   *  approximation isn't available. Frontend hides the chip on null. */
  guardianScoreDelta?: number | null
}

/* -------------------------------------------------------------------------- */
/* Input shape                                                                */
/* -------------------------------------------------------------------------- */

export interface PostureBriefPillarScores {
  compliance?: number
  bpa?: number
  /** Identity pillar surface — member MFA adoption %. */
  mfa?: number
  /** Aux: M365 Secure Score %. Reserved for future templates. */
  secureScore?: number
}

/**
 * Pillar facts for the lede (blueprint §6.2 row 1). Each comes from a query
 * the Command Center already runs: user insights (`noMfaEnabledUsers`,
 * `noMfaGlobalAdmins`), the violation summary (open and critical counts) and
 * dashboard stats (`activeDrifts`, a count of resources with drift). Leave a
 * field undefined while its query loads; never pass 0 for "unknown".
 */
export interface PostureBriefFacts {
  identity?: { noMfaMembers?: number; noMfaGlobalAdmins?: number }
  bpa?: { failing?: number; critical?: number }
  compliance?: { driftedResources?: number }
}

export interface PostureBriefInput {
  /** Signed-in user email. Unused since the byline became the provenance line
   *  (rule 5 bans bylines); kept for call-site compatibility. */
  userEmail?: string
  /** When Guardian last assessed the tenant (`lastAssessmentAt()`): null when
   *  never, undefined while unknown. Feeds the provenance line. */
  assessedAt?: string | null
  /** The provenance clock's "today"; tests only. */
  now?: Date
  /** Active tenant ID. Callers still pass this; unused by the composer now
   *  that copy is deterministic rather than seeded per tenant per day. */
  tenantId?: string
  /** Composite Guardian Score (0-100). */
  score?: number | null
  pillarScores?: PostureBriefPillarScores
  /** 30-day score delta: drives the verdict, the headline's direction read and the lede's delta clause. */
  deltas?: { score?: number; days?: number }
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity issue count — drives the headline and verdict. */
  criticalCount?: number
  /** Pillar facts for the action-first lede. Optional: without them the lede
   *  names the lowest pillar and its score. */
  facts?: PostureBriefFacts
  /** The top priority action's href. The composer owns no routes: with it, the
   *  brief carries `primaryAction`; without it (or on an all-clear), none.
   *  With `noData`, it is the Ad-Hoc Scans route, passed only for a viewer who
   *  may run a scan (a Platform Admin); everyone else gets the schedule copy
   *  (`firstAssessmentAction`). */
  primaryHref?: string
  /** True when the score is absent because no assessment has run (not because
   *  a query is loading). Only read when `score` is null. The copy names no
   *  time: even a scheduled tenant's first scan comes at no time the brief
   *  can promise. */
  noData?: boolean
  /** With `noData`: Tenant Management, passed only for a viewer who may edit
   *  the scan schedule (GlobalAdmin or SecurityAdmin). */
  scheduleSettingsHref?: string
  /** With `noData`: the tenant's scan schedule (`useTenantScanSchedule`); 'pending' or omitted claims none. */
  scanSchedule?: ScanScheduleState
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Pillar display names for the headline, lede and verdict (D23: the compliance
 * pillar reads "Baseline", as in the nav and the Guardian Score waffle key).
 * `emphasis.pillar` is set only when `emphasis.text` is one of these
 * (Subject-Ink Rule, §6.3). Every verdict template fits six words with
 * "Best Practice".
 */
const PILLAR_NAME: Record<Pillar, string> = {
  compliance: 'Baseline',
  bpa: 'Best Practice',
  identity: 'Identity',
}

function normalizePillars(
  raw?: PostureBriefPillarScores,
): { compliance?: number; bpa?: number; identity?: number } {
  if (!raw) return {}
  return {
    compliance: typeof raw.compliance === 'number' ? raw.compliance : undefined,
    bpa: typeof raw.bpa === 'number' ? raw.bpa : undefined,
    identity: typeof raw.mfa === 'number' ? raw.mfa : undefined,
  }
}

/**
 * Returns the lowest-scoring pillar, but ONLY when all three pillar scores
 * are defined. If any pillar is `undefined` (still loading, or a steady-state
 * `null` like "no starred BPA frameworks"), we cannot honestly call any
 * pillar "the weakest" — naming one based on a partial set lies to the user
 * (the historical bug: `bpa` arriving last meant `identity` got named as
 * weakest while BPA was actually lower once it loaded).
 *
 * Returning `undefined` here routes the verdict / headline / lede
 * branches into their no-laggard fallback copy, which doesn't name
 * a pillar.
 */
function weakestPillar(
  pillars: { compliance?: number; bpa?: number; identity?: number },
): { pillar: Pillar; score: number } | undefined {
  const order: Pillar[] = ['identity', 'bpa', 'compliance']
  // Bail if any pillar is missing — refuse to name a weakest from partial data.
  for (const p of order) {
    if (typeof pillars[p] !== 'number') return undefined
  }
  let best: { pillar: Pillar; score: number } | undefined
  for (const p of order) {
    const s = pillars[p] as number
    if (!best || s < best.score) best = { pillar: p, score: s }
  }
  return best
}

/** Strip trailing sentence punctuation from an action title before it's
 *  re-embedded in a new sentence (avoids "Enforce MFA.. first."). */
function stripTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/** Word form for 1-9, digits from 10 up — used by the routine-watch verdict's
 *  still-open critical count so it reads as prose. */
function countWord1to9(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : String(n)
}

/** Finite, non-negative critical count, or `undefined` when the caller
 *  didn't supply one. An unknown count must never be treated as zero. */
function normalizeCritical(n: number | undefined): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  ROUTINE_WATCH: 'Routine watch only.',
  CRIT_STILL_ONE: 'One critical item still open.',
  CRIT_STILL_FEW: '{n} critical items still open.',
  PULLING_DOWN: '{pillar} is lowering the score.',
  SCORE_DOWN: 'Score down {n} this month.',
  LIFT_NEXT: 'Lift {pillar} next.',
  LEAD_NEXT: 'Take the lead item next.',
  LIFT_FIRST: 'Lift {pillar} first.',
  START_LEAD: 'Start with the lead item.',
  NEEDS_WORK: '{pillar} needs the most work.',
  WORK_QUEUE: 'Work the queue top down.',
}

/**
 * Action-first h1 (blueprint §6.2 row 1): the headline names one action.
 * Precedence: the ranker's top action, then the laggard pillar, then the
 * critical count, then the all-clear or routine line.
 */
const HEADLINE_ACTION = {
  LOADING: 'Reading your posture.',
  TOP_ACTION: '{title} first.',
  LAGGARD: 'Start with {pillar} this week.',
  CRIT_ONE: 'Close the critical item first.',
  CRIT_FEW: 'Close the {n} critical items first.',
  CLEAR: 'Nothing to close this week.',
  CADENCE: 'Keep the usual review cadence this week.',
  QUEUE: 'Work the queue top down this week.',
}

/**
 * The one primary button, naming the subject of the headline's action. Counts
 * come only from known facts; without one the label stays generic.
 */
const PRIMARY_ACTION = {
  MEMBERS_ONE: 'Review the member',
  MEMBERS_FEW: 'Review the {n} members',
  DRIFTED_ONE: 'Review the drifted resource',
  DRIFTED_FEW: 'Review the {n} drifted resources',
  CONTROL: 'Open {title}',
  GENERIC: 'Open the top item',
}

/**
 * The lede: one sentence, one bold run naming the subject and its count. When
 * the headline names a pillar (the laggard, or the top action's pillar), the
 * lede speaks that pillar's facts (the shared `LEDE_FRAGMENTS`); without them
 * it names the lowest pillar; otherwise it names the Guardian Score. When the
 * top action leads on a pillar that scores above the lowest one, the lede
 * names the lowest pillar and says the queue's top fix is elsewhere
 * (`TOP_ELSEWHERE`), so the reader is not left to square the headline with
 * the pillar scores. The ordering claim is backed by `topAction`, the queue's
 * #1 row. It names the pillar, never the action's title: identity and drift
 * titles come from the server unvoiced (an em-dash, raw resource types), and
 * the primary button already names the item.
 */
const LEDE = {
  LOADING: '**Guardian is reading your tenant**.',
  NO_DATA: '**Guardian has not assessed this tenant yet**, so the score shows here after its first assessment.',
  LOWEST_PILLAR: '**{pillar} is the lowest pillar, at {score}**',
  TOP_ELSEWHERE: ", but the queue's top fix is in {pillar}",
  SCORE: '**Your Guardian Score is {score}**',
  SCORE_UP: ', up {n} in {d} days',
  SCORE_DOWN: ', down {n} in {d} days',
  SCORE_FLAT: ', unchanged in {d} days',
  SCORE_CLEAR: ', with no critical items open',
}

/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

/**
 * `lead` is the laggard pillar ONLY when the headline leads with it (see the
 * coherence rule above); otherwise undefined, and the verdict names no pillar.
 * `openWork` is the same `hasOpenWork` signal the headline reads, so known
 * open work never gets "Routine watch only." beside the queue headline.
 */
function pickVerdict(
  score: number,
  bucket: DeltaBucket,
  lead: Pillar | undefined,
  n: string,
  criticalCount: number | undefined,
  hasTopAction: boolean,
  openWork: boolean,
): string {
  const negative = isNegative(bucket)
  const laggard = lead !== undefined
  const pillarName = lead ? PILLAR_NAME[lead] : ''

  // The all-clear tier starts at 85 (not 90) to match the headline's
  // "clear" fallback threshold (`score >= 85`) — otherwise a score of
  // 85-89 with a known-zero critical count says "clear" in the headline
  // while the verdict still insisted there's a lead item to
  // take, which the coherence lint (and a careless reader) both catch.
  if (score >= 85 && !negative) {
    const c = criticalCount ?? 0
    if (c >= 1) {
      if (c === 1) return VERDICT.CRIT_STILL_ONE
      return VERDICT.CRIT_STILL_FEW.replace('{n}', countWord1to9(c))
    }
    if (laggard) return VERDICT.LIFT_NEXT.replace('{pillar}', pillarName)
    if (hasTopAction || openWork) return VERDICT.LEAD_NEXT
    return VERDICT.ROUTINE_WATCH
  }

  if (score >= 75) {
    if (negative) {
      if (laggard) return VERDICT.PULLING_DOWN.replace('{pillar}', pillarName)
      return VERDICT.SCORE_DOWN.replace('{n}', n)
    }
    if (laggard) return VERDICT.LIFT_NEXT.replace('{pillar}', pillarName)
    return VERDICT.LEAD_NEXT
  }

  if (score >= 60) {
    if (laggard) return VERDICT.LIFT_FIRST.replace('{pillar}', pillarName)
    return VERDICT.START_LEAD
  }

  if (laggard) return VERDICT.NEEDS_WORK.replace('{pillar}', pillarName)
  return VERDICT.WORK_QUEUE
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 1)                        */
/* -------------------------------------------------------------------------- */

type HeadlineKind = 'topAction' | 'laggard' | 'crit' | 'clear' | 'routine'

function pickActionHeadline(
  topAction: PostureBriefInput['topAction'] | undefined,
  laggard: boolean,
  weakest: { pillar: Pillar; score: number } | undefined,
  criticalCount: number | undefined,
  score: number,
  bucket: DeltaBucket,
  openWork: boolean,
): { kind: HeadlineKind; headline: string; emphasis?: ActionBriefFields['emphasis'] } {
  if (topAction?.title) {
    const name = PILLAR_NAME[topAction.pillar]
    // The action's own title stays on the primary button; the headline names its pillar.
    if (name) {
      return {
        kind: 'topAction',
        headline: HEADLINE_ACTION.LAGGARD.replace('{pillar}', name),
        emphasis: { text: name, pillar: topAction.pillar },
      }
    }
    const title = cap(stripTrailingPunct(topAction.title))
    return { kind: 'topAction', headline: HEADLINE_ACTION.TOP_ACTION.replace('{title}', title) }
  }
  if (laggard && weakest) {
    const name = PILLAR_NAME[weakest.pillar]
    return {
      kind: 'laggard',
      headline: HEADLINE_ACTION.LAGGARD.replace('{pillar}', name),
      emphasis: { text: name, pillar: weakest.pillar },
    }
  }
  if (criticalCount === 1) return { kind: 'crit', headline: HEADLINE_ACTION.CRIT_ONE }
  if (criticalCount !== undefined && criticalCount >= 2) {
    return { kind: 'crit', headline: HEADLINE_ACTION.CRIT_FEW.replace('{n}', String(criticalCount)) }
  }
  if (criticalCount === 0 && score >= 85 && !openWork) return { kind: 'clear', headline: HEADLINE_ACTION.CLEAR }
  // The cadence line is calm; any known open work gets the queue line instead.
  const headline =
    score >= 85 && !isNegative(bucket) && !openWork ? HEADLINE_ACTION.CADENCE : HEADLINE_ACTION.QUEUE
  return { kind: 'routine', headline }
}

/** True when any known fact shows open work. An unknown count is never zero. */
function hasOpenWork(facts: PostureBriefFacts | undefined): boolean {
  return [
    facts?.compliance?.driftedResources,
    facts?.bpa?.failing,
    facts?.bpa?.critical,
    facts?.identity?.noMfaMembers,
  ].some((n) => (knownCount(n) ?? 0) >= 1)
}

/** The named pillar's facts as a lede body, or `undefined` when the facts
 *  hold no open count to name (a zero or unknown count falls back). */
function pillarFactsBody(pillar: Pillar, facts: PostureBriefFacts | undefined): string | undefined {
  if (pillar === 'identity') {
    return mfaLedeBody(facts?.identity?.noMfaMembers, facts?.identity?.noMfaGlobalAdmins)
  }
  if (pillar === 'bpa') {
    const failing = knownCount(facts?.bpa?.failing)
    const critical = knownCount(facts?.bpa?.critical)
    if ((failing ?? 0) < 1 && (critical ?? 0) < 1) return undefined
    return controlsLedeBody(failing, critical)
  }
  const drifted = knownCount(facts?.compliance?.driftedResources)
  return drifted !== undefined && drifted >= 1 ? driftedLedeBody(drifted) : undefined
}

/** The primary button label for a non-clear headline; the subject is the
 *  headline's pillar, and an unknown or zero count falls back to the generic. */
function primaryLabel(
  kind: HeadlineKind,
  topAction: PostureBriefInput['topAction'] | undefined,
  weakest: { pillar: Pillar; score: number } | undefined,
  facts: PostureBriefFacts | undefined,
): string {
  const subject: Pillar | undefined =
    kind === 'topAction' ? topAction?.pillar : kind === 'laggard' ? weakest?.pillar : undefined
  if (subject === 'identity') {
    const n = knownCount(facts?.identity?.noMfaMembers)
    if (n !== undefined && n >= 1) {
      return n === 1 ? PRIMARY_ACTION.MEMBERS_ONE : PRIMARY_ACTION.MEMBERS_FEW.replace('{n}', String(n))
    }
  } else if (subject === 'compliance') {
    const n = knownCount(facts?.compliance?.driftedResources)
    if (n !== undefined && n >= 1) {
      return n === 1 ? PRIMARY_ACTION.DRIFTED_ONE : PRIMARY_ACTION.DRIFTED_FEW.replace('{n}', String(n))
    }
  } else if (subject === 'bpa' && kind === 'topAction' && topAction?.title) {
    return PRIMARY_ACTION.CONTROL.replace('{title}', cap(stripTrailingPunct(topAction.title)))
  }
  return PRIMARY_ACTION.GENERIC
}

/**
 * "**Baseline is the lowest pillar, at 36**, but the queue's top fix is in Best
 * Practice" (no closing period), when the top action's pillar scores above the
 * lowest pillar. Scores compare as the rounded figures the pillar tiles show,
 * so the lede never calls a pillar the lowest beside an equal number.
 * `undefined` when the top action is on the lowest pillar.
 */
function topElsewhereBody(
  topPillar: Pillar,
  weakest: { pillar: Pillar; score: number },
  pillars: { compliance?: number; bpa?: number; identity?: number },
): string | undefined {
  const top = pillars[topPillar]
  const lowest = Math.round(weakest.score)
  if (weakest.pillar === topPillar || typeof top !== 'number' || Math.round(top) <= lowest) return undefined
  return (
    LEDE.LOWEST_PILLAR.replace('{pillar}', PILLAR_NAME[weakest.pillar]).replace('{score}', String(lowest)) +
    LEDE.TOP_ELSEWHERE.replace('{pillar}', PILLAR_NAME[topPillar])
  )
}

function buildLede(
  kind: HeadlineKind,
  topAction: PostureBriefInput['topAction'] | undefined,
  weakest: { pillar: Pillar; score: number } | undefined,
  pillars: { compliance?: number; bpa?: number; identity?: number },
  facts: PostureBriefFacts | undefined,
  score: number,
  delta: { dir: 'up' | 'down' | 'flat'; n: string; days: number } | undefined,
): string {
  if (kind === 'topAction' && topAction && weakest) {
    const body = topElsewhereBody(topAction.pillar, weakest, pillars)
    if (body) return `${body}.`
  }
  const subject: Pillar | undefined =
    kind === 'topAction' ? topAction?.pillar : kind === 'laggard' ? weakest?.pillar : undefined
  if (subject) {
    const body =
      pillarFactsBody(subject, facts) ??
      (weakest && weakest.pillar === subject
        ? LEDE.LOWEST_PILLAR.replace('{pillar}', PILLAR_NAME[subject]).replace(
            '{score}',
            String(Math.round(weakest.score)),
          )
        : undefined)
    if (body) return `${body}.`
  }
  const head = LEDE.SCORE.replace('{score}', String(Math.round(score)))
  if (kind === 'clear') return `${head}${LEDE.SCORE_CLEAR}.`
  if (!delta) return `${head}.`
  const clause =
    delta.dir === 'up' ? LEDE.SCORE_UP : delta.dir === 'down' ? LEDE.SCORE_DOWN : LEDE.SCORE_FLAT
  return `${head}${clause.replace('{n}', delta.n).replace('{d}', String(delta.days))}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a PostureBrief from the active posture state. All inputs are
 * optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band "fragile / multiple gaps" verdict. Without this
 * guard the composer would coerce `null → 0`, which lands the loading state
 * in the lowest editorial band and shows the user an authoritative-but-wrong
 * verdict for a fraction of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value (e.g. a brand-new tenant
 * with no MFA adoption) and still routes through the normal band logic —
 * only `null`/`undefined` triggers the loading brief.
 */
export function composePostureBrief(input: PostureBriefInput = {}): PostureBrief & ActionBriefFields {
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  const byline = briefProvenance(input.assessedAt, { now: input.now })

  if (input.score == null && input.noData) {
    const first = firstAssessmentAction({
      scanHref: input.primaryHref,
      settingsHref: input.scheduleSettingsHref,
      schedule: input.scanSchedule,
    })
    return {
      verdict: 'Not assessed yet',
      byline,
      delta: null,
      headline: first.headline,
      lede: LEDE.NO_DATA,
      ...(first.primaryAction ? { primaryAction: first.primaryAction } : {}),
    }
  }

  if (input.score == null) {
    return {
      verdict: 'Reading posture…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const score = input.score
  const bucket = bucketDelta(deltaValue)
  const dir: 'up' | 'down' | 'flat' = deltaValue > 0 ? 'up' : deltaValue < 0 ? 'down' : 'flat'
  const n = formatDelta(Math.abs(deltaValue))

  const pillars = normalizePillars(input.pillarScores)
  const weakest = weakestPillar(pillars)
  const laggard = !!(weakest && weakest.score < 70)
  const criticalCount = normalizeCritical(input.criticalCount)

  const openWork = hasOpenWork(input.facts)
  const action = pickActionHeadline(input.topAction, laggard, weakest, criticalCount, score, bucket, openWork)
  // One first move: the verdict may name the laggard only when the headline leads with it.
  const lead = laggard && weakest && action.emphasis?.pillar === weakest.pillar ? weakest.pillar : undefined
  const verdict = pickVerdict(score, bucket, lead, n, criticalCount, !!input.topAction?.title, openWork)
  // The lede's delta clause speaks only when the caller passed a real delta;
  // the 0 default above must never read as "unchanged".
  const deltaKnown = typeof input.deltas?.score === 'number'
  const lede = buildLede(
    action.kind,
    input.topAction,
    weakest,
    pillars,
    input.facts,
    score,
    deltaKnown ? { dir, n, days: deltaDays } : undefined,
  )

  const href = input.primaryHref?.trim()
  return {
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
    headline: action.headline,
    ...(action.emphasis ? { emphasis: action.emphasis } : {}),
    lede,
    ...(href && action.kind !== 'clear'
      ? { primaryAction: { label: primaryLabel(action.kind, input.topAction, weakest, input.facts), href } }
      : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  LEDE,
  LEDE_FRAGMENTS,
  PRIMARY_ACTION,
  PILLAR_NAME,
  FIRST_ASSESSMENT,
}
